import {
  type AttendanceVerificationMethodConfig,
  type GetAttendanceVerificationResponse,
  type UpdateAttendanceVerificationBody,
  type UpdateAttendanceVerificationResponse,
  UpdateAttendanceVerificationResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { put } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";

const ATTENDANCE_VERIFICATION_API_TYPE = "workplace-attendance-verification";

interface UpdateAttendanceVerificationParams {
  workplaceId: string;
  methods: AttendanceVerificationMethodConfig[];
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError<{ message?: string }>(error) && isDefined(error.response?.data?.message)) {
    return error.response.data.message;
  }

  return "Failed to update timekeeping methods";
}

export function useUpdateAttendanceVerification(
  options: UseMutationOptions<
    UpdateAttendanceVerificationResponse,
    Error,
    UpdateAttendanceVerificationParams
  > = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateAttendanceVerificationParams) => {
      const { workplaceId, methods } = params;

      const requestBody: UpdateAttendanceVerificationBody = {
        data: [
          {
            type: ATTENDANCE_VERIFICATION_API_TYPE,
            id: workplaceId,
            attributes: {
              methods,
            },
          },
        ],
      };

      const response = await put<
        UpdateAttendanceVerificationBody,
        UpdateAttendanceVerificationResponse
      >({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/facilityprofile/${workplaceId}/attendance-verification`,
        data: requestBody,
        responseSchema: UpdateAttendanceVerificationResponseSchema,
      });

      return response.data;
    },
    onSuccess: (data, variables) => {
      const url = `${environmentConfig.REACT_APP_BASE_API_URL}/facilityprofile/${variables.workplaceId}/attendance-verification`;
      queryClient.setQueryData<GetAttendanceVerificationResponse>([url], data);
    },
    onError: (error) => {
      showErrorToast(getErrorMessage(error));
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_FACILITY_PROFILE_FAILED,
      userSuccessMessage: "Timekeeping methods updated successfully",
    },
    ...options,
  });
}
